import React, { useState, useEffect, useRef, useCallback } from 'react';
import './MvpModal.css';

/* ============================================
   CONFETTI ENGINE
============================================ */
function useConfetti(canvasRef, active) {
  useEffect(() => {
    if (!active || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let particles = [];

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const colors = [
      '#00E599', '#FF6B35', '#5B8AF5', '#A78BFA',
      '#FFD700', '#FF4D94', '#00D4FF', '#FF8C00',
      '#7CFF5B', '#FF5B5B', '#C77DFF', '#00FFC8',
    ];

    // Launch confetti from bottom
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: canvas.height + Math.random() * 100,
        vx: (Math.random() - 0.5) * 8,
        vy: -(Math.random() * 18 + 8),
        w: Math.random() * 10 + 4,
        h: Math.random() * 6 + 3,
        color: colors[Math.floor(Math.random() * colors.length)],
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 12,
        gravity: 0.15 + Math.random() * 0.1,
        opacity: 1,
        decay: 0.003 + Math.random() * 0.003,
      });
    }

    // Second wave delayed
    setTimeout(() => {
      for (let i = 0; i < 80; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: canvas.height + Math.random() * 50,
          vx: (Math.random() - 0.5) * 6,
          vy: -(Math.random() * 14 + 6),
          w: Math.random() * 8 + 3,
          h: Math.random() * 5 + 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * 360,
          rotationSpeed: (Math.random() - 0.5) * 10,
          gravity: 0.12 + Math.random() * 0.08,
          opacity: 1,
          decay: 0.004 + Math.random() * 0.003,
        });
      }
    }, 300);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles = particles.filter(p => p.opacity > 0.01);

      particles.forEach(p => {
        p.x += p.vx;
        p.vy += p.gravity;
        p.y += p.vy;
        p.rotation += p.rotationSpeed;
        p.vx *= 0.99;
        p.opacity -= p.decay;

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });

      if (particles.length > 0) {
        animId = requestAnimationFrame(animate);
      }
    };
    animId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, [active, canvasRef]);
}

/* ============================================
   SECTOR OPTIONS
============================================ */
const SECTORS = [
  'E-commerce / Marketplace',
  'SaaS / Herramienta digital',
  'Fintech / Pagos',
  'AgTech / Agro',
  'Salud / HealthTech',
  'Educación / EdTech',
  'Logística / Transporte',
  'Gastronomía / Food',
  'Inmobiliaria / PropTech',
  'Comunidad / Red social',
  'Otro',
];

const MATURITY_OPTIONS = [
  { value: 'idea', label: 'Solo una idea', icon: '💡' },
  { value: 'research', label: 'Investigué el mercado', icon: '🔍' },
  { value: 'prototype', label: 'Tengo un prototipo/mockup', icon: '📐' },
  { value: 'validated', label: 'Ya validé con usuarios', icon: '✅' },
];

/* ============================================
   FORMAT FILE SIZE
============================================ */
function formatFileSize(bytes) {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

/* ============================================
   MVP MODAL COMPONENT
============================================ */
function MvpModal({ isOpen, onClose }) {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    celular: '',
    titulo: '',
    sector: '',
    madurez: '',
    expectativas: '',
    descripcion: '',
    hosting: 'incluido',
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);

  useConfetti(canvasRef, showConfetti);

  // Trigger confetti on open
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Lock body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset form on close
  const handleClose = useCallback(() => {
    setShowConfetti(false);
    setSuccess(false);
    setError('');
    setFormData({ nombre: '', email: '', celular: '', titulo: '', sector: '', madurez: '', expectativas: '', descripcion: '', hosting: 'incluido' });
    setFiles([]);
    onClose();
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') handleClose(); };
    if (isOpen) window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, handleClose]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles(prev => [...prev, ...newFiles].slice(0, 5)); // max 5 files
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      const payload = new FormData();
      payload.append('nombre', formData.nombre);
      payload.append('email', formData.email);
      payload.append('celular', formData.celular);
      payload.append('titulo', formData.titulo);
      payload.append('sector', formData.sector);
      payload.append('madurez', formData.madurez);
      payload.append('expectativas', formData.expectativas);
      payload.append('descripcion', formData.descripcion);
      payload.append('hosting', formData.hosting);
      files.forEach(file => payload.append('archivos', file));

      const response = await fetch('/api/mvp/request', {
        method: 'POST',
        body: payload,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Error al enviar la solicitud');
      }

      setSuccess(true);
      setShowConfetti(true); // second confetti burst on success
    } catch (err) {
      setError(err.message || 'Error de conexión. Intentá de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const isValid = formData.nombre.trim() && formData.email.trim() && formData.celular.trim() && formData.titulo.trim() && formData.sector && formData.madurez;

  return (
    <>
      <canvas ref={canvasRef} className="confetti-canvas" />
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-container" onClick={(e) => e.stopPropagation()}>

          {success ? (
            /* ======= SUCCESS STATE ======= */
            <div className="success-state">
              <div className="success-icon">🚀</div>
              <div className="success-title">¡Solicitud enviada!</div>
              <p className="success-message">
                Evaluaremos tu MVP y te contactaremos en menos de <strong>48 horas</strong> para darle curso a tu proyecto.
              </p>
              <p className="success-message">
                Revisá tu correo — te enviaremos los próximos pasos.
              </p>
              <button className="success-close-btn" onClick={handleClose}>
                Cerrar
              </button>
            </div>
          ) : (
            /* ======= FORM STATE ======= */
            <>
              <div className="modal-header">
                <button className="modal-close" onClick={handleClose} aria-label="Cerrar">
                  ×
                </button>
                <div className="modal-badge">
                  <div className="modal-badge-dot" />
                  Nuevo MVP
                </div>
                <div className="modal-title">Contanos tu idea</div>
                <p className="modal-subtitle">
                  Completá este formulario y evaluaremos tu proyecto en menos de 48hs. Sin compromiso.
                </p>
              </div>

              <div className="modal-body">
                <form onSubmit={handleSubmit}>

                  {/* Nombre */}
                  <div className="form-group">
                    <label className="form-label">
                      Nombre <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Tu nombre completo"
                      value={formData.nombre}
                      onChange={(e) => handleChange('nombre', e.target.value)}
                      maxLength={100}
                      required
                    />
                  </div>

                  {/* Email y Celular en fila */}
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">
                        Email <span className="required">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-input"
                        placeholder="tu@email.com"
                        value={formData.email}
                        onChange={(e) => handleChange('email', e.target.value)}
                        maxLength={120}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        Celular <span className="required">*</span>
                      </label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="099 123 456"
                        value={formData.celular}
                        onChange={(e) => handleChange('celular', e.target.value)}
                        maxLength={20}
                        required
                      />
                    </div>
                  </div>

                  {/* Título del MVP */}
                  <div className="form-group">
                    <label className="form-label">
                      Título de tu MVP <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder='Ej: "App de turnos para clínicas"'
                      value={formData.titulo}
                      onChange={(e) => handleChange('titulo', e.target.value)}
                      maxLength={120}
                      required
                    />
                  </div>

                  {/* Sector */}
                  <div className="form-group">
                    <label className="form-label">
                      Sector donde se desarrolla el MVP <span className="required">*</span>
                    </label>
                    <select
                      className="form-select"
                      value={formData.sector}
                      onChange={(e) => handleChange('sector', e.target.value)}
                      required
                    >
                      <option value="" disabled>Seleccioná un sector</option>
                      {SECTORS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Madurez */}
                  <div className="form-group">
                    <label className="form-label">
                      Nivel de madurez de la idea <span className="required">*</span>
                    </label>
                    <div className="radio-cards">
                      {MATURITY_OPTIONS.map(opt => (
                        <label className="radio-card" key={opt.value}>
                          <input
                            type="radio"
                            name="madurez"
                            value={opt.value}
                            checked={formData.madurez === opt.value}
                            onChange={(e) => handleChange('madurez', e.target.value)}
                          />
                          <div className="radio-card-body">
                            <span className="rc-icon">{opt.icon}</span>
                            <span>{opt.label}</span>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Expectativas a 3 meses */}
                  <div className="form-group">
                    <label className="form-label">
                      Expectativas a 3 meses luego de lanzar
                      <span className="form-hint">— ¿Qué esperás lograr?</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      placeholder="Ej: Tener 50 usuarios activos, validar si el modelo de negocio funciona, conseguir primeros clientes pagos..."
                      value={formData.expectativas}
                      onChange={(e) => handleChange('expectativas', e.target.value)}
                      rows={3}
                    />
                  </div>

                  {/* Descripción libre */}
                  <div className="form-group">
                    <label className="form-label">
                      Indicanos más sobre tu idea
                      <span className="form-hint">— Cuantos más detalles, mejor</span>
                    </label>
                    <textarea
                      className="form-textarea"
                      placeholder="Describí el problema que resolvés, quiénes son tus usuarios, qué funcionalidades imaginás, referencias de productos similares..."
                      value={formData.descripcion}
                      onChange={(e) => handleChange('descripcion', e.target.value)}
                      rows={4}
                    />
                  </div>

                  {/* File upload */}
                  <div className="form-group">
                    <label className="form-label">
                      ¿Tenés documentación?
                      <span className="form-hint">— Mockups, specs, presentaciones</span>
                    </label>
                    <div
                      className={`file-upload-area ${files.length > 0 ? 'has-files' : ''}`}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="upload-icon">📎</div>
                      <div className="upload-text">
                        <strong>Hacé click para subir archivos</strong>
                        <br />
                        o arrastrá y soltá acá
                      </div>
                      <div className="upload-formats">PDF, DOC, DOCX, PNG, JPG, PPTX — máx. 10MB c/u — hasta 5 archivos</div>
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.pptx,.ppt,.xls,.xlsx,.txt"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    {files.length > 0 && (
                      <div className="file-list">
                        {files.map((file, i) => (
                          <div className="file-item" key={`${file.name}-${i}`}>
                            <div className="file-item-info">
                              <span>📄</span>
                              <span className="file-item-name">{file.name}</span>
                              <span className="file-item-size">{formatFileSize(file.size)}</span>
                            </div>
                            <button type="button" className="file-remove" onClick={() => removeFile(i)}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Hosting preference */}
                  <div className="form-group">
                    <label className="form-label">
                      Preferencia de hosting
                    </label>
                    <div className="radio-cards">
                      <label className="radio-card">
                        <input
                          type="radio"
                          name="hosting"
                          value="incluido"
                          checked={formData.hosting === 'incluido'}
                          onChange={(e) => handleChange('hosting', e.target.value)}
                        />
                        <div className="radio-card-body">
                          <span className="rc-icon">☁️</span>
                          <span>Hosting incluido en tuMVP.uy</span>
                        </div>
                      </label>
                      <label className="radio-card">
                        <input
                          type="radio"
                          name="hosting"
                          value="propio"
                          checked={formData.hosting === 'propio'}
                          onChange={(e) => handleChange('hosting', e.target.value)}
                        />
                        <div className="radio-card-body">
                          <span className="rc-icon">🖥️</span>
                          <span>Instalación propia</span>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Error */}
                  {error && (
                    <div style={{
                      padding: '12px 16px',
                      background: '#FF4D4D15',
                      border: '1px solid #FF4D4D30',
                      borderRadius: '10px',
                      color: '#FF6B6B',
                      fontSize: '0.85rem',
                      marginBottom: '20px',
                    }}>
                      {error}
                    </div>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    className="modal-submit"
                    disabled={!isValid || submitting}
                  >
                    {submitting ? (
                      <>
                        <div className="spinner" />
                        Enviando...
                      </>
                    ) : (
                      'Solicitar evaluación →'
                    )}
                  </button>

                </form>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}

export default MvpModal;
